import React, { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import "./HeroSection.css";

/* Images */
import img2 from "../assets/images/2.png";
import img3 from "../assets/images/3.jpg";
import img4 from "../assets/images/4.png";
import img5 from "../assets/images/5.jpg";
import img6 from "../assets/images/6.jpg";
import img7 from "../assets/images/7.png";

/* Christmas tree */
import christmasTree from "../assets/images/christmas-tree.png";

const DURATION = 5;
const INTERVAL_MS = 5000;

const HeroSection = ({ backgroundAnimation = "zoom" }) => {
  const images = [img2, img3, img4, img5, img6, img7];

  const stackRef = useRef(null);
  const progressRef = useRef(null);
  const snowRef = useRef(null);

  const intervalRef = useRef(null);
  const transitionTlRef = useRef(null);
  const startAutoRef = useRef(null);
  const isAnimatingRef = useRef(false);

  // Store GSAP animations for each slide's background zoom
  const zoomAnimsRef = useRef([]);

  const [stack, setStack] = useState(images);
  const [snowActive, setSnowActive] = useState(false);

  /* Christmas */
  const [showChristmas, setShowChristmas] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  /* ===========================
     SNOW (DEC 1 – JAN 31)
  =========================== */
  useEffect(() => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const d = now.getDate();
    setSnowActive((m === 12 && d >= 1) || (m === 1 && d <= 31));
  }, []);

  /* ===========================
     CHRISTMAS COUNTDOWN
     REAL DATES ONLY
     DEC 20 → DEC 25
  =========================== */
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();

    const start = new Date(year, 11, 20, 0, 0, 0);
    const end = new Date(year, 11, 25, 23, 59, 59);

    if (now < start || now > end) {
      setShowChristmas(false);
      return;
    }

    setShowChristmas(true);

    const interval = setInterval(() => {
      const diff = end - new Date();

      if (diff <= 0) {
        clearInterval(interval);
        setShowChristmas(false);
        return;
      }

      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  /* ===========================
     SNOW CANVAS
  =========================== */
  useEffect(() => {
    if (!snowActive) return;

    const canvas = snowRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener("resize", resize);

    const flakes = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 1,
      d: Math.random() + 0.5
    }));

    let raf;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "white";
      ctx.beginPath();

      flakes.forEach(f => {
        ctx.moveTo(f.x, f.y);
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
      });

      ctx.fill();

      flakes.forEach(f => {
        f.y += f.d;
        f.x += Math.sin(f.y / 20) * 0.7;
        if (f.y > canvas.height) {
          f.y = 0;
          f.x = Math.random() * canvas.width;
        }
      });

      raf = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [snowActive]);

  /* ===========================
     BACKGROUND ZOOM ANIMATION
  =========================== */
  useEffect(() => {
    if (backgroundAnimation !== "zoom" || !stackRef.current) return;

    const slides = stackRef.current.children;
    zoomAnimsRef.current = [];

    Array.from(slides).forEach(slide => {
      const anim = gsap.fromTo(
        slide,
        { scale: 1 },
        {
          scale: 1.15,
          duration: 12,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true
        }
      );
      zoomAnimsRef.current.push(anim);
    });

    return () => {
      zoomAnimsRef.current.forEach(anim => anim.kill());
    };
  }, [backgroundAnimation, stack]); // Re-apply when stack rotates

  /* ===========================
     PROGRESS BAR
  =========================== */
  const restartProgress = useCallback(() => {
    if (!progressRef.current) return;
    gsap.killTweensOf(progressRef.current);
    gsap.set(progressRef.current, { width: 0 });
    gsap.to(progressRef.current, {
      width: "100%",
      duration: DURATION,
      ease: "linear"
    });
  }, []);

  const clearAuto = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const animateTransition = useCallback(
    (isNext = true, resetTimer = true) => {
      if (!stackRef.current || isAnimatingRef.current) return;

      const slides = stackRef.current.children;
      if (slides.length < 2) return;

      isAnimatingRef.current = true;
      if (transitionTlRef.current) transitionTlRef.current.kill();

      // Pause background zoom during transition
      zoomAnimsRef.current.forEach(anim => anim.timeScale(0));

      const current = slides[0];
      const nextSlide = isNext ? slides[1] : slides[slides.length - 1];

      const tl = gsap.timeline({
        onComplete: () => {
          setStack(prev => {
            const arr = [...prev];
            isNext ? arr.push(arr.shift()) : arr.unshift(arr.pop());
            return arr;
          });

          restartProgress();
          isAnimatingRef.current = false;

          // Resume background zoom after stack update (useEffect will re-create them)
          if (resetTimer && startAutoRef.current) startAutoRef.current();
        }
      });

      transitionTlRef.current = tl;

      tl.to(current, { scale: 1.12, opacity: 0, duration: 0.9 });
      tl.fromTo(
        nextSlide,
        { scale: 1.05, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.9 },
        0
      );
    },
    [restartProgress]
  );

  const startAuto = useCallback(() => {
    clearAuto();
    intervalRef.current = setInterval(() => {
      animateTransition(true, false);
    }, INTERVAL_MS);
  }, [clearAuto, animateTransition]);

  useEffect(() => {
    startAutoRef.current = startAuto;
  }, [startAuto]);

  useEffect(() => {
    restartProgress();
    startAuto();
    return clearAuto;
  }, [restartProgress, startAuto, clearAuto]);

  return (
    <>
      <div className="hero-root">
        {snowActive && <canvas ref={snowRef} className="hero-snow" />}

        <div className="hero-gradient" />

        <ul ref={stackRef} className="hero-stack">
          {stack.map((img, i) => (
            <li
              key={i}
              className="hero-slide"
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
        </ul>

        <div className="hero-center">
          <h1 className="hero-title">Welcome to Ceymos Lanka</h1>
        </div>

        {showChristmas && (
          <div className="christmas-widget">
            <img src={christmasTree} alt="Christmas Tree" />
            <div className="countdown">
              <span>{countdown.days}d</span>
              <span>{countdown.hours}h</span>
              <span>{countdown.minutes}m</span>
              <span>{countdown.seconds}s</span>
            </div>
          </div>
        )}

        <button className="hero-btn left" onClick={() => animateTransition(false, true)}>
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>

        <button className="hero-btn right" onClick={() => animateTransition(true, true)}>
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
      </div>

      <div className="progress-container-outside">
        <div ref={progressRef} className="progress-bar" />
      </div>
    </>
  );
};

export default HeroSection;